import React, { useEffect, useState } from "react";
import { QuestionBaseForm } from "@/Components/Questions/QuestionBaseForm";
import { useAtom } from "jotai";
import { usePage } from "@inertiajs/inertia-react";
import { Inertia } from "@inertiajs/inertia";
import { editQuestionAtom } from "@/Stores";
import { toast } from "react-toastify";
import { uuid } from "@/Utilities/Utilities";
import { QuestionRemovalButton } from "@/Pages/Reporting/Owner/Edit";
import LibraryDialogCenter from "@/Components/Library/LibraryDialogCenter";

export function EditQuestion({
  question,
  showReporterSelect = true,
  showAddTeams = false,
}) {
  const organization = usePage().props?.organization?.data || {};
  const team = usePage().props?.team?.data || null;

  const [questions, setQuestions] = useAtom(editQuestionAtom);
  const [disabled, setDisabled] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (question && question.id !== questions.id) {
      fetch(`/questions/${question.id}`)
        .then((response) => response.json())
        .then((data) => {
          const inputs = data.inputs.map((i) => ({ ...i, uuid: uuid() }));
          const tags =
            question.tags?.map((t) => ({ label: t.name, value: t.id })) || [];
          const user_id = question?.pivot?.user_id || null;
          const team_id = question?.pivot?.team_id || null;
          const teams = question?.teams?.map((t) => t.id) || [];

          const existingData = {
            ...data,
            inputs,
            tags,
            organization_id: organization.id,
            errorBag: `updateQuestion.${question.id}`,
          };

          if (showAddTeams) {
            existingData.teams = teams;
            delete existingData.team_id;
            delete existingData.user_id;
          } else {
            existingData.user_id = user_id;
            existingData.team_id = team_id;
            delete existingData.teams;
          }

          setQuestions(existingData);
        });
    }

    // unset the question when the component unmounts
    return () => {
      setQuestions({});
    };
  }, [question]);

  let pagePrefix = "";
  const currentRoute = route()?.current();

  if (currentRoute.startsWith("admin.")) {
    pagePrefix = "admin.";
  } else if (currentRoute.startsWith("agency.")) {
    pagePrefix = "agency.";
  }

  function handleNewActivity(e, draft = false) {
    setDisabled(true);

    // Transform tags to match the backend
    const tags =
      questions.tags.map((t) => ({
        name: t.label,
        ...(t.value && { id: t.value }),
      })) || [];

    const payload = {
      ...questions,
      tags: tags,
      organization_id: organization.id,
    };

    const id = toast.loading("Please wait...");

    Inertia.put(
      route(`${pagePrefix}question.update`, { question: question.id, draft }),
      payload,
      {
        preserveScroll: true,
        only: ["questionTypes", "errors", "team"],
        onSuccess: () => {
          toast.update(id, {
            render: "The question was successfully updated!",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
          });
        },
        onError: (error) => {
          toast.update(id, {
            render: "Something went wrong, please review your input",
            type: toast.TYPE.ERROR,
            isLoading: false,
            autoClose: 5000,
          });
        },
        onFinish: () => {
          setDisabled(false);
        },
      },
    );

    e.preventDefault();
  }

  if (question.id !== questions.id) return <div>Loading...</div>;

  return (
    <>
      <QuestionBaseForm
        atom={editQuestionAtom}
        showReporterSelect={showReporterSelect}
        showAddTeams={showAddTeams}
      />
      <div className="flex w-full flex-row justify-end gap-4">
        {showAddTeams && (
          <button
            className="btn-red flex-grow-0"
            onClick={() => setShowDeleteModal(true)}
          >
            Delete activity
          </button>
        )}
        {team && <QuestionRemovalButton question={question} team={team} />}
        {questions?.is_published !== 1 && (
          <button
            disabled={disabled}
            className="btn-blue-outline flex-grow-0"
            onClick={(e) => handleNewActivity(e, true)}
          >
            Save as draft
          </button>
        )}
        <button
          disabled={disabled}
          className="btn btn-blue flex-grow-0"
          onClick={handleNewActivity}
        >
          Save activity
        </button>
      </div>

      {showAddTeams && (
        <LibraryDialogCenter
          question={question}
          showDeleteModal={showDeleteModal}
          setShowDeleteModal={setShowDeleteModal}
          back={false}
        />
      )}
    </>
  );
}
